import React from 'react'
import { put, select, take, takeEvery } from 'redux-saga/effects'
import { Map, Record } from 'immutable'
import i18next from 'i18next'
import * as Sentry from '@sentry/browser'

// @ts-ignore
import { store } from 'store'
// @ts-ignore
import { showInfo, showSuccess } from 'store/entities/toastr/utils'
import { getIdentityActivitiesHistoryItems, isOrganizationHasStories } from 'store/entities/identity/selectors'
import { triggerCommitOrDidPreviousActivities } from 'store/entities/localSettings/actions'
import { getActivitiesHistory, setCustomStory, updateActivity } from 'store/entities/identity/actions'
import { IActivitiesHistoryItem, ISetCustomStory } from 'store/entities/identity/interface'
import { modal } from 'store/entities/modal/actions'
import * as actions from 'app/constants/identityActions'
import ConfirmOrCancel from 'app/domains/common/Modals/ConfirmOrCancel/ConfirmOrCancel'
import MyActivitiesDangerouslyText from 'app/domains/routes/MyActivities/MyActivitiesDangerouslyText'
import ExtraBlock from 'app/domains/routes/MyActivities/MyActivitiesExtraBlock/MyActivitiesExtraBlock'
import FeedStoriesListItemMenuPopup from 'app/domains/common/Modals/FeedStoriesListItemMenuPopup/FeedStoriesListItemMenuPopup'

export function* handleOnCommitOrDidPreviousActivitiesProccess({ payload }: any) {
  try {
    const { userActivityId, action } = payload
    yield take(getActivitiesHistory.success)
    const activities = yield select(getIdentityActivitiesHistoryItems)
    const storiesIsEnabled = yield select(isOrganizationHasStories)
    const activityForActionIndex = activities.findIndex((activity: Record<IActivitiesHistoryItem>) => {
      return activity.get('userActivityId') === userActivityId
    }) as number
    // If activityForActionIndex is -1 it means that committed activity from previous level
    if (activityForActionIndex === -1) {
      showInfo({ content: 'You already finished this track' })
      return
    }

    const activityForAction = activities.get(activityForActionIndex) as Record<IActivitiesHistoryItem>
    const userClickedOnDid = action === actions.DID
    const userClickedOnCommit = action === actions.COMMIT
    const showStoryPopup = (storiesIsEnabled &&
      activityForAction.get('question') &&
      !activityForAction.get('hasStory')) as boolean
    const userActivityForActionId = activityForAction.get('userActivityId') as number
    if (activityForAction.get('actionType') === actions.DID) {
      showInfo({ content: 'You have already completed this activity' })
      yield showStoryPopupSaga({
        showStoryPopup,
        userActivityForActionId,
        userActivityIdForUpdatedActivity: userActivityForActionId,
        activityForAction,
        activityForActionIndex,
      })
      return
    } else {
      if (userClickedOnCommit) {
        showInfo({
          content:
            'You clicked on a previous activity. Complete it now now or use the same link to return once you have done it.',
        })
      }
      if (userClickedOnDid) {
        const content = showStoryPopup
          ? 'You have successfully completed this activity. Please share a story about how it went.'
          : 'You have successfully completed this activity'
        showSuccess({ content })
      }
    }

    const handleUpdateActivity = () => {
      store.dispatch(
        updateActivity({
          userActivityId: userActivityForActionId,
          actionTypeDto: {
            previousActionType: activityForAction.get('actionType'),
            newActionType: actions.DID,
          },
        })
      )
    }

    // show if user not completed activity but clicked on commit
    if (userClickedOnCommit) {
      const newProps = {
        image: activityForAction.get('image'),
        description: activityForAction.get('description'),
        name: activityForAction.get('name'),
        order: activityForActionIndex + 1,
        videoThumbnail: activityForAction.get('videoThumbnail'),
        videoId: activityForAction.get('activityVideoId'),
      }
      yield put(
        modal.show(ConfirmOrCancel, {
          size: 'sm',
          title: 'MicroActivity Not Completed',
          activityId: activityForAction.get('activityId'),
          userActivityId: userActivityForActionId,
          confirmBtn: i18next.t('user:Complete'),
          children: React.isValidElement(MyActivitiesDangerouslyText)
            ? React.cloneElement(MyActivitiesDangerouslyText, { ...newProps })
            : // @ts-ignore
              React.createElement(MyActivitiesDangerouslyText, { ...newProps }),
          handleSubmit: handleUpdateActivity,
        })
      )
    }

    // update if user clicked on did
    if (userClickedOnDid) {
      handleUpdateActivity()
    }

    const {
      payload: {
        data: { userActivityId: userActivityIdForUpdatedActivity },
      },
    } = yield take(updateActivity.success)
    yield showStoryPopupSaga({
      showStoryPopup,
      userActivityForActionId,
      userActivityIdForUpdatedActivity,
      activityForAction,
      activityForActionIndex,
    })
  } catch (error) {
    yield put(modal.hide())
    Sentry.withScope(scope => {
      scope.setExtra('error', error)
      scope.setExtra('payload', JSON.stringify(payload))
      scope.setExtra('where error was happen', 'handleOnCommitOrDidPreviousActivitiesProccess')
      Sentry.captureException(error)
    })
  }
}

export function* showStoryPopupSaga({
  showStoryPopup,
  userActivityForActionId,
  userActivityIdForUpdatedActivity,
  activityForAction,
  activityForActionIndex,
}: {
  showStoryPopup: boolean
  userActivityForActionId: number
  userActivityIdForUpdatedActivity: number
  activityForAction: Record<IActivitiesHistoryItem>
  activityForActionIndex: number
}) {
  if (
    showStoryPopup &&
    // https://github.com/prohabits/prohabits-ui/issues/4282 because user can close modal and completes another activity
    userActivityForActionId === userActivityIdForUpdatedActivity
  ) {
    yield put(
      modal.show(FeedStoriesListItemMenuPopup, {
        order: activityForActionIndex + 1,
        size: 'sm',
        activity: activityForAction,
        question: activityForAction.get('question'),
        activityId: activityForAction.get('activityId'),
        userActivityId: userActivityForActionId,
        initialValues: Map({ story: '' }),
        customSubmit: (data: ISetCustomStory) => store.dispatch(setCustomStory(data)),
        extraBlock: ExtraBlock,
      })
    )
  } else {
    yield put(modal.hide())
  }
}

export default function* onCommitOrDidPreviousActivitiesSaga() {
  yield takeEvery(triggerCommitOrDidPreviousActivities, handleOnCommitOrDidPreviousActivitiesProccess)
}
